import { useCallback } from 'react';

import { Trash2Icon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Button } from '@/components/ui/button';
import { useIntegrations } from '@/hooks/useIntegrations';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { Integration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { getChannelKey } from '@/util/channelName';

import { MODAL_DELETE } from '../../Modals/ModalConductor';

export const DeleteFieldset = ({ integration }: { integration: Integration }) => {
  const dispatch = useDispatch();
  const form = useFormContext();
  const { deleteIntegration, deleteStatus } = useIntegrations(
    integration.desk_id,
    integration.integration_id
  );
  const { t } = useTranslation();
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE)
  );
  const onClick = useCallback(() => {
    const channelName = t(getChannelKey(integration.type));
    const deleteProps = {
      subtitle: (
        <Trans
          i18nKey="integrations.fieldsets.delete_fieldset.warn_message"
          values={{ 0: channelName }}
        />
      ),
      confirm: true,
      onDelete: () => {
        form.reset();
        deleteIntegration({
          desk_id: integration.desk_id,
          integration_id: integration.integration_id,
        });
      },
    };
    dispatch(pushModal(MODAL_DELETE, deleteProps));
  }, [
    t,
    integration.type,
    integration.desk_id,
    integration.integration_id,
    dispatch,
    form,
    deleteIntegration,
  ]);

  return (
    <Button
      variant="destructive-ghost"
      className="w-auto"
      onClick={onClick}
      disabled={!canWrite}
      isLoading={deleteStatus === 'pending'}
    >
      <Trash2Icon />
      {t('integrations.fieldsets.delete_fieldset.button')}
    </Button>
  );
};

export default DeleteFieldset;
